/**
 * The question in front of copying a project: what to call the copy, and what
 * the copy carries.
 *
 * A dialog rather than a menu entry that just does it, and the name field is the
 * reason. Two rows called "Kliendi X koduleht" is a register nobody can read,
 * and the server refuses to invent the second name for exactly that reason - it
 * requires one in the body and has no default. So somebody has to be asked, and
 * the moment to ask is the same moment the switches are decided.
 *
 * ## The sentence about what is NOT copied is the point of the dialog
 *
 * A copy is a PLAN, not a HISTORY: the brief, the budget, the phases and the
 * tasks travel, while the hours, the invoices, the expenses, the contracts, the
 * files, the timeline and the dates do not. That rule is written in full on the
 * server (`crm-projects/lib/projectCopy.ts`) and it is a good rule, but it is
 * invisible from here - a copied project looks like a project, and an operator
 * who duplicates last year's job and then goes looking for its hours has no way
 * to tell "the copy dropped them" from "the CRM lost them". They find out by
 * asking somebody, days later, which is the expensive version of being told.
 *
 * So both halves are said before the button: `projects.duplicateWhat` names the
 * hours, invoices and expenses above the boxes, and `projects.duplicateNotCopied`
 * adds the contracts, the files, the timeline and the dates underneath. Neither
 * belongs in a tooltip. A sentence nobody reads is a sentence that is not there.
 *
 * ## Why the boxes start where they do
 *
 * Tasks, milestones and the flow map ON, team OFF. The route defaults all four
 * to false, which is right for an API answering a caller that said nothing; a
 * dialog is not that caller. The reason anybody reaches for this button is "we
 * did this job before, do it again", and the phases, the task list and the map
 * drawn over them ARE the job - a copy that arrives as an empty shell is a copy
 * that saved nobody anything. The team is the one switch where copying too much
 * is worse than copying too little: it assigns people to work that has not been
 * discussed with them, and on the tasks it does so line by line.
 *
 * The two are independent on purpose. Tasks with the milestones left off is a
 * legitimate ask - it lands every task in the "no milestone" column, which is a
 * plan that has not been phased yet rather than an error, and the server files
 * them there deliberately instead of pointing them at the source's phases.
 *
 * ## After the write
 *
 * The copy is opened. Staying here would leave the operator on the source
 * project with a receipt and no way to tell whether the thing they created
 * exists, and that is precisely the state in which a button gets pressed a
 * second time. The in-flight guard covers the same risk from the other side:
 * a copy has no idempotency key, so two clicks are two projects.
 *
 * ## One refusal is named, and the rest are not
 *
 * `copy_too_large` is the only failure this dialog can describe better than
 * "something went wrong", because it is the only one the operator can act on:
 * the source is over the route's ceiling, and the answer carries both counts and
 * both limits. Everything else - a dropped connection, a 500, a project deleted
 * while the dialog was open - is the same instruction (try again, then ask
 * somebody), so a wording per status code would be four sentences that all mean
 * one thing.
 *
 * Nothing can reach it on today's data: production's largest project is a
 * fraction of either limit. That is not a reason to leave it unbound. The
 * refusal exists precisely for the day the data grows, and a message that is
 * only written once the bug is reported arrives after the operator has already
 * decided the button is broken.
 */
import { useState } from "react";
import { Loader2 } from "lucide-react";
import { useT } from "../../../i18n/I18nProvider";
import { ApiError } from "../../../lib/api";
import { copyProject } from "../../../lib/projectCopy";
import type { Project } from "../../../lib/projects";
import { Btn, Modal, useToast } from "../../ui";
import { Field } from "../_shared/formBits";

/** One switch, drawn the way the time dialogs draw theirs. */
function Check({
  checked,
  onChange,
  label,
}: {
  checked: boolean;
  onChange: (v: boolean) => void;
  label: string;
}) {
  return (
    <label className="crm-inp cursor-pointer">
      <input type="checkbox" checked={checked} onChange={(e) => onChange(e.target.checked)} />
      <span className="text-[13.5px]">{label}</span>
    </label>
  );
}

/**
 * The four numbers behind a `copy_too_large`, or nothing.
 *
 * Every member is checked rather than trusted. `ApiError.body` is `unknown` by
 * design - it is whatever came back over the wire - and a partly-read payload
 * would print "undefined" into the middle of the sentence, which is a worse
 * failure message than the generic one it replaced.
 */
function tooLargeCounts(err: unknown): Record<string, number> | null {
  if (!(err instanceof ApiError) || err.status !== 400) return null;
  const body = err.body;
  if (!body || typeof body !== "object") return null;
  const row = body as Record<string, unknown>;
  if (row.error !== "copy_too_large") return null;
  const out: Record<string, number> = {};
  for (const key of ["tasks", "milestones", "maxTasks", "maxMilestones"]) {
    if (typeof row[key] !== "number") return null;
    out[key] = row[key] as number;
  }
  return out;
}

export function DuplicateProjectModal({
  project,
  onClose,
  onCreated,
}: {
  project: Project;
  onClose: () => void;
  /**
   * The new project, whole, as the server wrote it.
   *
   * The caller is expected to go to it - see the note above about the operator
   * who cannot tell whether anything happened. It gets the row rather than an id
   * so that a screen which wants to show the copy in place is able to, without
   * paying for a GET of a record that was just returned.
   */
  onCreated: (created: Project) => void;
}) {
  const t = useT();
  const toast = useToast();
  // The suffix comes from the catalogue, not from a string here: it is text the
  // operator reads and, if they accept it, the name every other screen shows.
  const [name, setName] = useState(() => t("projects.duplicateNameDefault", { name: project.name }));
  const [tasks, setTasks] = useState(true);
  const [milestones, setMilestones] = useState(true);
  const [team, setTeam] = useState(false);
  // On, like the phases and the tasks: the flow map is the same plan drawn as
  // arrows. The server drops its task pointers and clears its ticks on the copy.
  const [flowMap, setFlowMap] = useState(true);
  const [busy, setBusy] = useState(false);
  // The failure SENTENCE rather than a flag, so the one refusal worth naming
  // can say the four numbers the route sent with it.
  const [failed, setFailed] = useState<string | null>(null);

  const trimmed = name.trim();

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (busy || !trimmed) return;
    setBusy(true);
    setFailed(null);
    try {
      const created = await copyProject(project.id, { name: trimmed, tasks, milestones, team, flowMap });
      toast(t("projects.duplicateCreated"));
      onCreated(created);
    } catch (err) {
      // The dialog stays open and keeps what was typed. Closing on a failure
      // would put the operator back on the register with no copy and no reason
      // given, which reads as a button that does nothing - and the next thing
      // that happens is the button being pressed again.
      const counts = tooLargeCounts(err);
      setFailed(counts ? t("projects.duplicateTooLarge", counts) : t("common.error"));
      setBusy(false);
    }
  }

  return (
    <Modal
      title={t("projects.duplicateTitle")}
      label={t("projects.duplicateTitle")}
      onClose={onClose}
    >
      <form onSubmit={submit}>
        <Field label={t("projects.duplicateName")}>
          <label className="crm-inp">
            <input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder={t("projects.duplicateName")}
              autoComplete="off"
              autoFocus
              required
            />
          </label>
        </Field>

        {/* The heading is a sentence, not a word: it says what the boxes decide
            AND names the three things the copy never carries. */}
        <p className="crm-sub mt-3">{t("projects.duplicateWhat")}</p>
        <div className="mt-2 flex flex-col gap-2">
          <Check checked={tasks} onChange={setTasks} label={t("projects.duplicateTasks")} />
          <Check
            checked={milestones}
            onChange={setMilestones}
            label={t("projects.duplicateMilestones")}
          />
          <Check checked={flowMap} onChange={setFlowMap} label={t("projects.duplicateFlowMap")} />
          <Check checked={team} onChange={setTeam} label={t("projects.duplicateTeam")} />
        </div>

        {/* The rest of the omissions, under the boxes rather than in the heading
            above them: the heading is about a choice, this is about what no box
            can turn on. */}
        <p className="crm-mut mt-3 text-[12.5px]">{t("projects.duplicateNotCopied")}</p>

        {failed ? <p className="mt-2 text-[12.5px] text-red-600">{failed}</p> : null}

        <div className="crm-m-foot">
          <Btn type="button" onClick={onClose} disabled={busy}>
            {t("common.cancel")}
          </Btn>
          {/* Disabled while the request is in flight, and it stays disabled on
              the way out: `busy` is only cleared on failure, so the successful
              path cannot be clicked a second time in the frame between the
              answer arriving and the page changing. */}
          <Btn type="submit" tone="dark" disabled={busy || !trimmed}>
            {busy ? <Loader2 className="size-4 animate-spin" /> : null}
            {t("projects.duplicate")}
          </Btn>
        </div>
      </form>
    </Modal>
  );
}
